import React from 'react';
import { useNavigate } from 'react-router-dom';

// Define User interface (similar to Header.tsx)
interface User {
  name: string;
  email: string;
  picture?: string;
  [key: string]: any; // Consider refining this if possible
}

interface SidebarProps {
  activeView: 'list' | 'search' | 'documents-status' | 'user-config';
  user: User | null;
}

const Sidebar: React.FC<SidebarProps> = ({ activeView, user }) => {
  const isActive = (view: 'list' | 'search' | 'documents-status' | 'user-config') => activeView === view;
  const navigate = useNavigate();

  const baseClasses = "flex items-center w-full px-4 py-3 text-left rounded-lg transition-colors";
  const activeClasses = "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400";
  const inactiveClasses = "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700";
  
  // Handle navigation with a simplified function
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <aside className="w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full">
      <div className="p-5 flex-grow">
        <nav className="space-y-1">
          <button 
            onClick={() => {
              handleNavigation('/documents');
            }}
            className={`${baseClasses} ${isActive('list') ? activeClasses : inactiveClasses}`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Elenco Documenti
          </button>
          
          <button
            onClick={() => {
              handleNavigation('/search');
            }}
            className={`${baseClasses} ${isActive('search') ? activeClasses : inactiveClasses}`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Cerca
          </button>
          

        </nav>
      </div>
      
      {/* Admin section */}
      <div className="p-5 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
          Amministrazione
        </h3>
        <nav className="space-y-1">
          <button
            onClick={() => {
              handleNavigation('/documents/status');
            }}
            className={`${baseClasses} ${isActive('documents-status') ? activeClasses : inactiveClasses}`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Indicizzazione
          </button>
          
          <button
            onClick={() => {
              handleNavigation('/configurazioni');
            }}
            className={`${baseClasses} ${isActive('user-config') ? activeClasses : inactiveClasses}`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Configurazioni
          </button>
        </nav>
      </div>
      
      <div className="p-5 border-t border-gray-200 dark:border-gray-700">
        {user ? (
          <div className="flex items-center space-x-3">
            {user.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="h-8 w-8 rounded-full"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate" title={user.name}>{user.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={user.email}>{user.email}</p>
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-500 dark:text-gray-400">Accesso non effettuato</span>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;